import { NavLink, useNavigate } from 'react-router-dom'

import { useMe } from '@/api/hooks/useMe'
import { useAuth } from '@/auth/useAuth'
import { isAddonModule } from '@/modules/module-keys'
import {
  GROUP_LABELS,
  modulesInGroup,
  type ModuleDefinition,
  type ModuleGroup,
} from '@/modules/registry'
import { useModuleActivation } from '@/modules/useModules'
import { cn } from '@/shared/lib/cn'
import { ActivationMark } from '@/shared/ui/ActivationMark'
import { Button } from '@/shared/ui/Button'

import { ThemeToggle } from './ThemeToggle'

const GROUPS: ModuleGroup[] = ['lavoro', 'moduli', 'account']

export interface NavRailProps {
  /** Chiude il pannello su schermi stretti dopo aver scelto una voce. */
  onNavigate?: () => void
  onToggleActivity: () => void
  activityOpen: boolean
}

/**
 * Navigazione principale, e in fondo tutto ciò che vale per l'applicazione
 * intera: crediti, tema, identità, registro.
 *
 * Stanno qui e non in una barra in alto perché sono controlli di contorno:
 * una barra a piena larghezza dava a quattro comandi rari lo spazio più
 * pregiato della pagina, sopra il contenuto. Ai piedi di una colonna larga
 * 232px restano a un colpo d'occhio e non rubano niente alla vista.
 *
 * Due segnali distinti, che non si contendono lo stesso spazio:
 * la pagina corrente è la superficie sollevata più il testo pieno; il segno
 * di attivazione dice se un modulo opzionale è attivo. Un modulo spento
 * resta cliccabile — porta alla sua pagina, che spiega cosa contiene.
 */
export function NavRail({ onNavigate, onToggleActivity, activityOpen }: NavRailProps) {
  const { session, logout } = useAuth()
  const navigate = useNavigate()
  const { isActive } = useModuleActivation()
  // 'aziende' è l'unico modulo riservato agli amministratori: un controllo
  // diretto qui basta, non serve un sistema di ruoli generico per un caso
  // solo. Il backend rifiuta comunque la richiesta a chi non è admin.
  const isAdmin = useMe().data?.role === 'admin'

  return (
    <div className="flex min-h-full flex-col">
      <nav aria-label="Moduli della dashboard" className="flex flex-col gap-6 py-4">
        {GROUPS.map((group) => {
          const label = GROUP_LABELS[group]
          const modules = modulesInGroup(group).filter(
            (module) => module.key !== 'aziende' || isAdmin,
          )

          return (
            <div key={group}>
              {label ? <p className="type-eyebrow px-4 pb-2">{label}</p> : null}
              <ul className="flex flex-col">
                {modules.map((module) => (
                  <NavItem
                    key={module.key}
                    module={module}
                    moduleActive={isActive(module.key)}
                    onNavigate={onNavigate}
                  />
                ))}
              </ul>
            </div>
          )
        })}
      </nav>

      {/* `mt-auto`: il piede sta in fondo alla colonna quando i moduli non la
          riempiono, e subito sotto l'ultima voce quando la superano. */}
      <div className="mt-auto flex flex-col gap-0.5 border-t border-[var(--hairline)] px-1.5 py-2">
        {/* TODO: valore fisso finché /credits non esiste sul backend reale
            (nessuna rotta in infra/api_stack.py) — la richiesta vera resta
            bloccata sullo skeleton di caricamento per sempre, da qui il
            rettangolo vuoto. Ripristinare useCredits() quando l'endpoint c'è. */}
        <p className="type-data text-thread-400 px-2.5 py-1.5">
          <span className="text-linen-200">100</span> crediti
        </p>

        <Button
          variant="quiet"
          size="sm"
          className="w-full justify-start"
          onClick={onToggleActivity}
          aria-expanded={activityOpen}
          aria-controls="pannello-attivita"
        >
          Attività
        </Button>

        <ThemeToggle />

        {session ? (
          <div className="flex items-center justify-between gap-2 pl-2.5">
            <span className="text-thread-400 text-meta min-w-0 truncate">{session.username}</span>
            <Button
              variant="quiet"
              size="sm"
              onClick={() => {
                logout()
                void navigate('/login', { replace: true })
              }}
            >
              Esci
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

interface NavItemProps {
  module: ModuleDefinition
  moduleActive: boolean
  onNavigate?: () => void
}

function NavItem({ module, moduleActive, onNavigate }: NavItemProps) {
  const showsActivationMark = isAddonModule(module.key)
  const isOff = showsActivationMark && !moduleActive

  return (
    <li>
      <NavLink
        to={module.path}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'flex items-stretch gap-3 pr-4 transition-colors duration-[var(--dur-fast)]',
            isActive ? 'bg-ink-800' : 'hover:bg-ink-800/60',
          )
        }
      >
        {({ isActive }) => (
          <>
            {showsActivationMark ? (
              <ActivationMark active={moduleActive} animate className="my-1" />
            ) : (
              <span aria-hidden="true" className="w-[3px]" />
            )}

            <span
              className={cn(
                'text-base py-2',
                isActive
                  ? 'text-linen-100 font-medium'
                  : isOff
                    ? 'text-thread-500'
                    : 'text-thread-400',
                !isActive && 'hover:text-linen-200',
              )}
            >
              {module.name}
              {isOff ? <span className="sr-only"> (non attivo)</span> : null}
            </span>
          </>
        )}
      </NavLink>
    </li>
  )
}
